/**
 * Top-right glass toolbar — share / save view / fullscreen.
 *
 * Ported from webpages/viewer/src/ui/Toolbar.ts, trimmed for runtime use:
 *  - dropped FOV slider (the editor authors FOV; users rarely change it)
 *  - dropped Measure tool (admin feature)
 *  - dropped Gyro (touch motion-look) — can re-add later
 *  - dropped Orient / Thumbnail / StartPosition (admin only)
 *
 * The camera button owns the whole capture→delivery ladder (see saveView()).
 * Delivery lives here rather than in capture/ because this is where user
 * activation, resolveLang() and showToast() already are; capture/ stays
 * DOM-free and i18n-free and hands back pixels.
 */

import type * as pc from 'playcanvas';

import { CaptureError, captureFrame, captureFrameAsync, encodePng } from '../capture/capture-frame';
import { isWebGpu } from '../capture/frame-source';
import { drawWatermark } from '../capture/watermark';
import { resolveLang } from './lang';
import { SaveOverlay } from './save-overlay';
import { ICON_COG } from './settings-panel';

const ICON_SHARE = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 9.5a2.5 2.5 0 110-3l4-2.5M6 9.5l4 2.5M10 4a2 2 0 104 0 2 2 0 00-4 0zM10 12a2 2 0 104 0 2 2 0 00-4 0zM2 8a2 2 0 104 0 2 2 0 00-4 0z" stroke="currentColor" stroke-width="1.2"/></svg>';
const ICON_CAMERA = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 4h2l1-2h6l1 2h2a1 1 0 011 1v8a1 1 0 01-1 1H2a1 1 0 01-1-1V5a1 1 0 011-1z" stroke="currentColor" stroke-width="1.2"/><circle cx="8" cy="8.5" r="2.5" stroke="currentColor" stroke-width="1.2"/></svg>';
const ICON_FULLSCREEN = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M2 6V2h4M10 2h4v4M14 10v4h-4M6 14H2v-4" stroke="currentColor" stroke-width="1.2"/></svg>';
const ICON_GRID = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.2"/><rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.2"/><rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.2"/><rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" stroke-width="1.2"/></svg>';
const ICON_CLOSE = '<svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" stroke-width="1.2"/></svg>';

/**
 * shotSaved deliberately never says "gallery": on Android the PNG lands in
 * Download/, which Google Photos only surfaces under Menu ▸ Device folders.
 * And there is no "Saved to Photos" string anywhere, because share() resolves
 * without telling us which target the user picked — we refuse to guess.
 *
 * holdToSave names no menu item for the same reason. The rescue rung is reached
 * by any touch-first device without canShare({files}) — Firefox Android and any
 * embed whose iframe lacks allow="web-share" included — and only iOS calls the
 * row "Add to Photos"; Android's callout says "Download image". Naming a control
 * that does not exist on the platform in front of the user is worse than naming
 * none, on the one rung whose whole job is to be the last route to a file.
 */
const TOAST = {
    en: {
        copied: 'Link copied!',
        failed: 'Could not copy',
        shotSaved: 'Image saved',
        shotFailed: 'Could not save image',
        holdToSave: 'Touch and hold the image to save it',
        saveView: 'Save this view',
        close: 'Close',
        share: 'Share', save: 'Save', fullscreen: 'Fullscreen', menu: 'Menu'
    },
    es: {
        copied: '¡Enlace copiado!',
        failed: 'No se pudo copiar',
        shotSaved: 'Imagen guardada',
        shotFailed: 'No se pudo guardar la imagen',
        holdToSave: 'Mantén pulsada la imagen para guardarla',
        saveView: 'Guardar esta vista',
        close: 'Cerrar',
        share: 'Compartir', save: 'Guardar', fullscreen: 'Pantalla completa', menu: 'Menú'
    },
    ca: {
        copied: 'Enllaç copiat!',
        failed: 'No s’ha pogut copiar',
        shotSaved: 'Imatge desada',
        shotFailed: 'No s’ha pogut desar la imatge',
        holdToSave: 'Mantén premuda la imatge per desar-la',
        saveView: 'Desa aquesta vista',
        close: 'Tanca',
        share: 'Comparteix', save: 'Desa', fullscreen: 'Pantalla completa', menu: 'Menú'
    }
} as const;

/** Long enough to read a full sentence; the link toasts keep their 1500ms. */
const SHOT_TOAST_MS = 2200;

/** Second tap inside this window is swallowed. Started only AFTER the
 *  synchronous capture returns: started before it, the timer becomes due during
 *  the block and fires the instant the block ends, which debounces nothing. */
const SHOT_DEBOUNCE_MS = 400;

/**
 * Safety net for the share rung only. While a sheet is open the debounce cannot
 * be a timer at all — a second share() rejects InvalidStateError, and a sheet
 * can sit open for as long as the user likes — so `busy` is held until the
 * promise settles. This bounds the case where it never does (a platform bug
 * leaving the button dead forever is worse than a duplicate sheet).
 */
const SHARE_STUCK_MS = 30000;

/** An AbortError faster than this cannot be a user cancelling a sheet they
 *  saw — it is the platform refusing silently, so it must fall through. */
const SHEET_MIN_MS = 250;

/** Object URLs are revoked on a timer, never on the line after .click(): the
 *  download read is asynchronous and an inline revoke races it into a missing
 *  or 0-byte file. That was one of the four bugs in the old screenshot(). */
const URL_LIFETIME_MS = 30000;

/** Poll interval for the paint gate. Only runs while the scene is still
 *  unpainted (normally never — mount builds the toolbar after the gate), and it
 *  is a timer, so it adds nothing to the frame budget either way. */
const READY_POLL_MS = 250;

export interface ToolbarOptions {
    parent: HTMLElement;
    canvas: HTMLCanvasElement;
    container: HTMLElement;
    /** Needed to render out of band for the capture — see capture-frame.ts. */
    app: pc.AppBase;
    /** Scene title, used only to name the saved file. */
    sceneName?: string;
    /** Stand demo: no share, no save-view, no fullscreen (the app already
     *  fills the screen) — a Scenes button back to the menu instead. */
    demo?: boolean;
    /** What the Scenes button does. Default: go to /demo.html. */
    onScenes?: () => void;
    /**
     * "Has the scene actually painted?" Capturing before it has yields a valid
     * but empty #070707 rectangle, so the camera button stays disabled until
     * this is true. Passed in rather than read from window.__painted, which is
     * a TEST HOOK and must not become production API.
     */
    ready?: () => boolean;
    /**
     * Called once per capture, right after the synchronous block. mount.ts wires
     * this to the adaptive-quality controller's sample reset: the capture is one
     * long gap in the frame stream, and the FPS window cannot tell that gap from
     * a device that has genuinely fallen below target, so on a phone already
     * grazing 30fps it would step quality DOWN a second after the user pressed
     * save. Optional — the Toolbar works without it, it just costs a bogus step.
     */
    onCapture?: () => void;
}

type FsElement = HTMLElement & { webkitRequestFullscreen?: () => Promise<void> | void };
type FsDocument = Document & {
    webkitFullscreenElement?: Element | null;
    /** Older WebKit spelling — still what some iPadOS builds fill in. */
    webkitCurrentFullScreenElement?: Element | null;
    webkitIsFullScreen?: boolean;
    webkitExitFullscreen?: () => Promise<void> | void;
};
type ShareNavigator = Navigator & {
    share?: (data: ShareData) => Promise<void>;
    canShare?: (data: ShareData) => boolean;
};

/** True when SOME fullscreen path exists for `el`. iPhone Safari has NO
 *  Fullscreen API on non-video elements — not even prefixed. */
function fullscreenApiAvailable(el: FsElement): boolean {
    return typeof el.requestFullscreen === 'function' ||
           typeof el.webkitRequestFullscreen === 'function';
}

/**
 * True when the page embedding us is one of OURS (the olloport.com demo modal
 * or a site preview of it) — i.e. a parent that implements the
 * `olloport:fullscreen` postMessage protocol. Decided from document.referrer,
 * which for an iframe is the embedding page: olloport.com sends
 * strict-origin-when-cross-origin, so the origin always arrives even though the
 * path does not. A third-party site (a client's WordPress page) has no listener
 * for our message, so on iPhone — where there is no Fullscreen API at all — the
 * button used to do nothing there. Fail closed: no referrer = not ours.
 */
function embeddedByOlloport(): boolean {
    // The parent stamps ?embed=olloport on the iframe src. That is the primary
    // signal and the only one that cannot go missing: iPad Safari withholds
    // document.referrer from a cross-site iframe, which left Safari — and only
    // Safari — taking the browser-fullscreen path with its pinned exit button.
    try {
        if (new URLSearchParams(location.search).get('embed') === 'olloport') return true;
    } catch { /* fall through to the referrer check */ }
    try {
        const host = new URL(document.referrer).hostname;
        return host === 'olloport.com' ||
               host.endsWith('.olloport.com') ||
               host.startsWith('olloport-web-preview') && host.endsWith('.workers.dev') ||    // site preview worker (scripts/deploy.sh site --preview) — the branch previews are olloport-web-preview-<branch>.<subdomain>.workers.dev, so the old 'olloport-web-preview.' with a DOT matched none of them and every branch preview looked like a third-party site to us
               host === 'localhost';                // astro dev of the homepage
    } catch {
        return false;
    }
}

/** iPadOS (Safari, and Chrome — every iPad browser is WebKit). iPadOS reports
 *  a Mac user agent, so the tell is a "Mac" with a touchscreen; unlike
 *  `pointer: coarse`, attaching a trackpad or Magic Keyboard doesn't change it. */
function isIpadOS(): boolean {
    try {
        return navigator.maxTouchPoints > 1 && /Macintosh|iPad/.test(navigator.userAgent);
    } catch {
        return false;
    }
}

/**
 * Fullscreen is ours to draw only inside our own modal: the parent expands
 * the panel and the viewer shows its own small ✕ bubble beside the name.
 *
 * Everywhere else it is the real Fullscreen API, iPad included. On iPad that
 * is the only thing that hides Safari's URL bar and tabs, and it always comes
 * with iPadOS's own big exit ✕ in the top-left corner — drawn by the system
 * above the page, not hideable or movable by any website. Drawing fullscreen
 * ourselves there instead (tried 13/09/2026) removed the ✕ but left the URL
 * bar and tabs in view: "half full screen". So the standalone viewer on iPad
 * keeps the big ✕ and moves the scene name clear of it (.ollo-ipados in
 * viewer.css), with no small bubble — the big ✕ already exits.
 */
function ownsFullscreen(): boolean {
    return window.parent !== window && embeddedByOlloport();
}

function hostFullscreenParam(): boolean {
    try {
        return new URLSearchParams(location.search).get('hostfs') === '1';
    } catch {
        return false;
    }
}

/**
 * True when the platform can put THIS file into the OS share sheet.
 * canShare({files}) is the only honest test: it returns false — rather than
 * throwing — for Firefox (no files support on any platform) and for an iframe
 * that was not granted allow="web-share", which is exactly the degrade-quietly
 * behaviour the ladder wants. Same capability-check discipline as
 * fullscreenApiAvailable() above; no user-agent string is consulted anywhere in
 * this file.
 */
function shareFilesAvailable(file: File): boolean {
    const nav = navigator as ShareNavigator;
    if (typeof nav.share !== 'function' || typeof nav.canShare !== 'function') return false;
    try {
        return nav.canShare({ files: [file] });
    } catch {
        return false;
    }
}

/**
 * Touch-first device. Two decisions hang off it:
 *  - share is only the primary rung here (on desktop, Web Share is partial in
 *    Chrome < 128, absent in Firefox and usually absent on Linux Chrome, and on
 *    Windows it opens an unfamiliar OS flyout for what should be "save a file")
 *  - when share is unavailable or refused, a phone gets the rescue overlay
 *    rather than a download, because a file in Downloads is not in the gallery
 * A touchscreen Windows laptop reports hover: hover and so correctly takes the
 * desktop download path.
 */
function touchFirst(): boolean {
    try {
        return window.matchMedia('(pointer: coarse) and (hover: none)').matches;
    } catch {
        return false;
    }
}

/** olloport-<scene>-<yyyymmdd-hhmm>.png — a second, crop-proof attribution
 *  for an image that will be forwarded and resized. */
function shotFilename(sceneName: string | undefined): string {
    const slug = (sceneName ?? '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '')
        .slice(0, 40);
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    const stamp = `${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}-${p(d.getHours())}${p(d.getMinutes())}`;
    return `olloport-${slug ? `${slug}-` : ''}${stamp}.png`;
}

export class Toolbar {

    private root: HTMLDivElement;

    private canvas: HTMLCanvasElement;

    private container: HTMLElement;

    private toast: HTMLDivElement | null = null;

    private toastTimer = 0;

    private pseudoFullscreen = false;

    private app: pc.AppBase;

    private sceneName?: string;

    /** The scene has painted, so a capture would contain something. */
    private ready: () => boolean;

    private onCapture?: () => void;

    /** Set by dispose(). A share() rejection can arrive long after the viewer
     *  was torn down (the editor preview re-mounts while a sheet is open), and
     *  building a rescue card at that point would attach it to a dead tree. */
    private disposed = false;

    private readyTimer = 0;

    private cameraBtn: HTMLButtonElement | null = null;

    /** Stand demo: the Scenes button — the tutorial's last bubble points at it. */
    scenesBtn: HTMLButtonElement | null = null;

    /** The row itself. Stand demo: the quality dropdown is inserted into
     *  it, in front of the Scenes button, so it lines up with it by flex. */
    get element(): HTMLElement {
        return this.root;
    }

    /** A capture (and possibly an open share sheet) is in flight. */
    private busy = false;

    private busyTimer = 0;

    /** Live download URLs, revoked on a timer and on dispose — never inline
     *  after .click(). */
    private urlTimers: number[] = [];

    private objectUrls: string[] = [];

    private saveOverlay: SaveOverlay | null = null;

    /** The "leave the demo" bubble, shown only while fullscreen. Ours, not the
     *  modal's — see makeCloseButton(). */
    private closeBtn: HTMLButtonElement | null = null;

    private uiRoot: HTMLElement;

    private onFsChange: (() => void) | null = null;


    private shutter: HTMLDivElement | null = null;

    private shutterTimer = 0;

    /** Publishes the row's width as --ui-toolbar-w on the ui root. */
    private rowObserver: ResizeObserver | null = null;

    /** Phones: the cog that unfolds the options (hidden by CSS elsewhere). */
    private menuBtn: HTMLButtonElement | null = null;

    private onDocDown: ((e: PointerEvent) => void) | null = null;

    constructor({ parent, canvas, container, app, sceneName, ready, onCapture, demo, onScenes }: ToolbarOptions) {
        this.canvas = canvas;
        this.container = container;
        this.app = app;
        this.sceneName = sceneName;
        this.ready = ready ?? (() => true);
        this.onCapture = onCapture;

        this.root = document.createElement('div');
        this.root.className = 'ollo-toolbar';
        // Viewer: the row is a small pill in the bottom-right corner, as in
        // Nucleus4D (Ilias, 04/11/2026). The class goes on the mount so what
        // else lives at the bottom (VR button, joystick, on phones the hints,
        // guide and measure dock) can make room for it in viewer.css. The
        // stand demo keeps its top-right row.
        if (!demo) container.classList.add('ollo-has-dock');

        const t = TOAST[resolveLang()];

        // No Share button any more (Ilias, 04/11/2026). share() is kept,
        // unused, in case it comes back.

        // "Save this view", not "Screenshot": on a phone that word collides with
        // the hardware gesture and implies the UI chrome will be in the result —
        // which it never is, since every piece of chrome is DOM in a sibling
        // .ollo-ui div and the capture is pure scene. Not on the stand demo
        // (Lenny, 13/10/2026): nothing for a visitor to do with the file.
        if (demo) {
            this.scenesBtn = this.btn(ICON_GRID, 'Scenes', onScenes ?? (() => { window.location.href = '/demo.html'; }));
            this.root.appendChild(this.scenesBtn);
        } else {
            this.cameraBtn = this.btn(ICON_CAMERA, t.saveView, () => this.saveView(), t.save);
            this.root.appendChild(this.cameraBtn);
            this.syncReady();
        }

        // Fullscreen: real API when the browser has one; on iPhone (no API)
        // an embedded viewer asks its parent to expand the panel — or, on a
        // third-party site, opens itself standalone in a new tab (see
        // toggleFullscreen). Standalone on iPhone there is nothing the button
        // could do — the page already fills the screen — so don't show a dead
        // control.
        const embedded = window.parent !== window;
        // Opened straight into fullscreen by our homepage (&hostfs=1): the page
        // owns fullscreen and leaving it closes the demo, so there is nothing
        // for a fullscreen button to toggle — iPadOS's own ✕ is the way out.
        const hostFullscreen = embedded && embeddedByOlloport() && hostFullscreenParam();
        if (hostFullscreen) container.classList.add('ollo-host-fs');
        if (!demo && !hostFullscreen && (fullscreenApiAvailable(container as FsElement) || embedded)) {
            const fs = this.btn(ICON_FULLSCREEN, t.fullscreen, () => this.toggleFullscreen(), t.fullscreen);
            fs.classList.add('ollo-fs-btn');
            this.root.appendChild(fs);
        }

        if (!demo) this.makeMenu(t.menu);
        parent.appendChild(this.root);

        this.uiRoot = parent;
        // The row grows with every button another module adds (ruler,
        // settings), and on a phone it reaches the scene name. Its width goes
        // to CSS so the name stops short of it and ends in "…" (viewer.css,
        // .ollo-info-panel).
        if (typeof ResizeObserver === 'function') {
            this.rowObserver = new ResizeObserver(() => {
                parent.style.setProperty('--ui-toolbar-w', `${Math.ceil(this.root.offsetWidth)}px`);
            });
            this.rowObserver.observe(this.root);
        }
        this.makeCloseButton();
        // On iPad, real fullscreen brings iPadOS's big exit ✕ in the top-left
        // corner — standalone, or our homepage in fullscreen around this
        // iframe (.ollo-host-fs). viewer.css lines the top row up with it.
        if (isIpadOS()) container.classList.add('ollo-ipados');

        // Safari's anti-phishing panel ("It looks like you are typing while in
        // full screen") fires on key input in element fullscreen, and the
        // scheme picker's feedback form is typed into. Leave real fullscreen
        // the moment a field in the viewer is pressed or focused, before the
        // keyboard can send a key. Acts on the standalone viewer (the /test/
        // links). Inside our homepage's fullscreen (&hostfs=1) the fullscreen
        // is the parent's and can't be left from here — the homepage forwards
        // no ?ab= by default, so there is no form to type into.
        this.onFieldEngage = (e: Event) => {
            const target = e.target as Element | null;
            if (!target?.closest?.('input, textarea, [contenteditable="true"]')) return;
            if (this.inRealFullscreen()) this.exitRealFullscreen();
        };
        container.addEventListener('pointerdown', this.onFieldEngage, true);
        container.addEventListener('focusin', this.onFieldEngage);
    }

    private onFieldEngage: ((e: Event) => void) | null = null;

    private exitRealFullscreen(): void {
        const doc = document as FsDocument;
        try {
            if (typeof document.exitFullscreen === 'function') document.exitFullscreen().catch(() => {});
            else void doc.webkitExitFullscreen?.();
        } catch { /* refused — nothing sensible to do */ }
    }

    /**
     * The demo modal's own ✕ is a 28px glyph at the page's top-right. Fullscreen
     * puts the viewer under it, so it landed on top of the toolbar bubbles — and
     * in REAL fullscreen it isn't painted at all (only the fullscreen element's
     * subtree is), leaving no way out but the Fullscreen button. Both go away by
     * owning the control here: a bubble identical to the other three, parked to
     * the right of the scene name, and only while fullscreen. The parent still
     * closes the modal — it just does it on our postMessage.
     */
    private makeCloseButton(): void {
        // Only where we draw fullscreen ourselves (ownsFullscreen): our modal,
        // where the bubble closes the demo.
        if (!ownsFullscreen()) return;
        const t = TOAST[resolveLang()];
        const b = this.btn(ICON_CLOSE, t.close, () => this.requestClose());
        b.classList.add('ollo-close-btn');
        // The viewer keeps it in the toolbar (on phones it stands under the
        // cog, where Fullscreen was; Ilias, 05/11/2026). The stand demo parks
        // it beside the scene name, placed by syncCloseButton.
        if (this.container.classList.contains('ollo-has-dock')) this.root.appendChild(b);
        else this.uiRoot.appendChild(b);
        this.closeBtn = b;

        // Whether it SHOWS is CSS's job (:fullscreen / :-webkit-full-screen,
        // plus .ollo-fs-pseudo) — the browser knows it is fullscreen even when
        // no JS property we can read says so. JS only sets the left offset,
        // which needs a measurement CSS cannot do.
        this.onFsChange = () => this.syncCloseButton();
        document.addEventListener('fullscreenchange', this.onFsChange);
        document.addEventListener('webkitfullscreenchange', this.onFsChange);
        window.addEventListener('resize', this.onFsChange);
        this.syncCloseButton();
    }

    /**
     * Real Fullscreen API, as opposed to the parent expanding its panel.
     * Deliberately paranoid: iPadOS reports this through any of three legacy
     * spellings, and a strict `=== container` also misses the case where the
     * element that went fullscreen is an ancestor or a descendant of ours. The
     * first version of this checked one property with `===`, read false on
     * iPad, and the close bubble silently never appeared.
     */
    private inRealFullscreen(): boolean {
        const doc = document as FsDocument;
        const el = document.fullscreenElement
                ?? doc.webkitFullscreenElement
                ?? doc.webkitCurrentFullScreenElement
                ?? null;
        if (el) return el === this.container || el.contains(this.container) || this.container.contains(el);
        return doc.webkitIsFullScreen === true;
    }

    /** Fullscreen, by either route. */
    private isFullscreen(): boolean {
        return this.pseudoFullscreen || this.inRealFullscreen();
    }

    /**
     * Park the bubble one toolbar gap to the right of the info panel, so it
     * reads as part of the scene-name row however long the name is. No panel
     * (a scene with no title) → it simply takes the panel's own left inset.
     */
    private syncCloseButton(): void {
        const b = this.closeBtn;
        if (!b || b.parentElement === this.root) return;
        // Re-measure across the fullscreen transition rather than once at the
        // start of it: the panel's own left shifts (CSS moves it clear of the
        // browser's exit button) and the viewport resizes, and which lands
        // first is not ours to decide.
        const place = () => {
            const panel = this.uiRoot.querySelector('.ollo-info-panel');
            const host = this.container.getBoundingClientRect();
            const left = panel
                ? panel.getBoundingClientRect().right - host.left + 8
                : 16;
            b.style.left = `${Math.round(left)}px`;
        };
        place();
        requestAnimationFrame(place);
        window.setTimeout(place, 150);
        window.setTimeout(place, 400);
    }

    /** Leave fullscreen first — a modal torn down underneath an active
     *  fullscreen element leaves the browser in it with nothing to show. */
    private requestClose(): void {
        const doc = document as FsDocument;
        if ((document.fullscreenElement ?? doc.webkitFullscreenElement) === this.container) {
            try {
                if (typeof document.exitFullscreen === 'function') document.exitFullscreen().catch(() => {});
                else void doc.webkitExitFullscreen?.();
            } catch { /* refused — the parent still gets the message */ }
        }
        this.pseudoFullscreen = false;
        this.container.classList.remove('ollo-fs-pseudo');
        this.syncCloseButton();
        try {
            window.parent.postMessage({ type: 'olloport:close' }, '*');
        } catch { /* sandboxed parent */ }
    }

    /** Ask the parent to give the panel the whole viewport, and back again. */
    private goPseudoFullscreen(): void {
        this.pseudoFullscreen = !this.pseudoFullscreen;
        this.container.classList.toggle('ollo-fs-pseudo', this.pseudoFullscreen);
        try {
            window.parent.postMessage({ type: 'olloport:fullscreen', on: this.pseudoFullscreen }, '*');
        } catch { /* sandboxed parent */ }
        // The panel resizes on the parent's side, so measure after it has.
        window.setTimeout(() => this.syncCloseButton(), 320);
    }

    /**
     * Phones (Ilias, 04/11/2026): the pill becomes a cog in the top-right
     * corner; tapping it unfolds the options downwards as named rows, and
     * picking one folds them again (so does a tap elsewhere). On wider
     * screens viewer.css hides the cog and shows the pill as it is.
     */
    private makeMenu(label: string): void {
        const m = document.createElement('button');
        m.type = 'button';
        m.className = 'ollo-dock-menu';
        m.innerHTML = ICON_COG;
        m.setAttribute('aria-label', label);
        m.setAttribute('aria-expanded', 'false');
        m.addEventListener('click', () => this.setMenuOpen(!this.root.classList.contains('open')));
        this.root.appendChild(m);
        this.menuBtn = m;
        this.root.addEventListener('click', (e) => {
            const b = (e.target as Element).closest('.ollo-toolbar-btn');
            if (b && this.root.contains(b)) window.setTimeout(() => this.setMenuOpen(false), 0);
        });
        this.onDocDown = (e) => {
            if (!this.root.contains(e.target as Node)) this.setMenuOpen(false);
        };
        document.addEventListener('pointerdown', this.onDocDown);
    }

    private setMenuOpen(on: boolean): void {
        if (!this.menuBtn) return;
        this.root.classList.toggle('open', on);
        this.menuBtn.setAttribute('aria-expanded', String(on));
    }

    /** A button from another module (the measure tool), placed first so the
     *  share / save / fullscreen order the visitor knows stays put. */
    addButton(svg: string, label: string, onClick: () => void, short?: string): HTMLButtonElement {
        const b = this.btn(svg, label, onClick, short);
        this.root.insertBefore(b, this.root.firstChild);
        return b;
    }

    /** `short` is the name printed under the icon in the dock (viewer.css
     *  shows it on wide screens only); the full label is for screen readers. */
    private btn(svg: string, label: string, onClick: () => void, short?: string): HTMLButtonElement {
        const b = document.createElement('button');
        b.className = 'ollo-btn ollo-toolbar-btn';
        b.innerHTML = svg;
        const name = document.createElement('span');
        name.className = 'ollo-toolbar-label';
        name.textContent = short ?? label;
        b.appendChild(name);
        // aria-label only: the bar shows it as a label under the icon on
        // hover (viewer.css), and a title would pop a second, native one.
        b.setAttribute('aria-label', label);
        b.addEventListener('click', onClick);
        return b;
    }

    private async share(): Promise<void> {
        const url = window.location.href;
        const t = TOAST[resolveLang()];

        // Native share sheet where available (phones/tablets). Inside the
        // marketing-site iframe this additionally needs allow="web-share" on
        // the frame. AbortError = the user closed the sheet — not a failure.
        const nav = navigator as Navigator & { share?: (d: { url: string }) => Promise<void> };
        if (typeof nav.share === 'function') {
            try {
                await nav.share({ url });
                return;
            } catch (err) {
                if ((err as { name?: string })?.name === 'AbortError') return;
                // fall through to clipboard
            }
        }

        // Async clipboard — blocked in a cross-origin iframe unless the frame
        // grants allow="clipboard-write" (the homepage modal does now).
        try {
            await navigator.clipboard.writeText(url);
            this.showToast(t.copied);
            return;
        } catch { /* fall through to the legacy path */ }

        // There used to be a third rung here: legacy execCommand('copy'), which
        // works by creating a hidden textarea and FOCUSING it. Focusing a text
        // field while the page is fullscreen is exactly what makes iPad Safari
        // throw its anti-phishing panel — "typing is not allowed in full screen
        // websites… may be showing a fake keyboard" — naming olloport.com as
        // the suspect. Gating it on a JS fullscreen check was not enough:
        // iPadOS does not reliably admit to being fullscreen (see
        // inRealFullscreen), so the guard read false and the panel came back.
        // A rung that can frighten a visitor about our own site is not worth a
        // copy that only helps browsers with no async clipboard at all.
        this.showToast(t.failed);
    }

    /**
     * Keep the camera button honest: a capture taken before the splat has
     * painted is a valid, empty #070707 rectangle, and a disabled affordance is
     * cheaper and more truthful than three languages of "please wait".
     */
    private syncReady(): void {
        if (!this.cameraBtn) return;
        if (this.ready()) {
            this.cameraBtn.disabled = false;
            this.cameraBtn.removeAttribute('aria-disabled');
            if (this.readyTimer) {
                window.clearInterval(this.readyTimer);
                this.readyTimer = 0;
            }
            return;
        }
        this.cameraBtn.disabled = true;
        this.cameraBtn.setAttribute('aria-disabled', 'true');
        if (this.readyTimer) return;
        this.readyTimer = window.setInterval(() => this.syncReady(), READY_POLL_MS);
    }

    /**
     * Capture → watermark → deliver, in one tap.
     *
     * The capture and the delivery decision run in a SINGLE synchronous task,
     * and that is not a style choice: on WebKit the user gesture is a
     * call-stack token, not Chromium's five-second window, so a single await
     * before navigator.share() turns it into a silent NotAllowedError — no
     * sheet, no error, a button that looks dead. That is precisely how the old
     * screenshot() behaved on iPhone.
     *
     * Every branch below ends in either a real image or a translated toast.
     */
    private saveView(): void {
        const t = TOAST[resolveLang()];

        // A second share() while a sheet is open rejects InvalidStateError, and
        // a double-tap on a phone is effortless. No timer yet: see
        // SHOT_DEBOUNCE_MS — one started here would expire during the block.
        if (this.disposed || this.busy || !this.ready()) return;
        this.busy = true;

        // WebGPU: the readback is async (see captureFrameAsync), so the
        // delivery runs without the click's activation and the ladder's own
        // fallbacks (download / hold-to-save overlay) take over.
        if (isWebGpu(this.app)) {
            captureFrameAsync(this.app, this.canvas)
                .then((shot) => { if (!this.disposed) this.deliver(shot); })
                .catch(() => {
                    this.busy = false;
                    this.showToast(t.shotFailed, SHOT_TOAST_MS);
                });
            return;
        }

        let shot: HTMLCanvasElement;
        try {
            shot = captureFrame(this.app, this.canvas);
        } catch {
            this.busy = false;
            this.showToast(t.shotFailed, SHOT_TOAST_MS);
            return;
        }
        this.deliver(shot);
    }

    /** Watermark, encode, and run the delivery ladder on a captured frame. */
    private deliver(shot: HTMLCanvasElement): void {
        const t = TOAST[resolveLang()];
        let file: File;
        let dataUrl: string;
        try {
            // The context already exists (capture-frame created it opaque and
            // willReadFrequently); a second getContext returns that same one,
            // and any options passed here would be ignored.
            const ctx = shot.getContext('2d');
            if (!ctx) throw new CaptureError('no 2d context');
            // Composited BEFORE the encode, so the mark is inside the File and
            // therefore inside the share, the download and the overlay's <img>
            // alike — one image, one truth. No blank re-probe afterwards:
            // captureFrame already refused an unwritten buffer, and a watermark
            // can only ADD pixels, so the check could never fire and its
            // getImageData calls came straight out of the gesture budget.
            drawWatermark(ctx, shot.width, shot.height);
            ({ file, dataUrl } = encodePng(shot, shotFilename(this.sceneName)));
        } catch {
            this.busy = false;
            this.showToast(t.shotFailed, SHOT_TOAST_MS);
            return;
        }

        // The frame stream just had a hole punched in it; tell whoever measures
        // frame times to forget it rather than treat it as a slow device.
        this.onCapture?.();
        this.flashShutter();

        if (touchFirst()) {
            // On a phone the share sheet is the ONLY route to the camera roll,
            // and the sheet itself is the feedback — hence no toast on success.
            // shareFile owns `busy` from here: the sheet outlives any timer.
            if (shareFilesAvailable(file)) {
                this.shareFile(file, dataUrl);
                return;
            }
            // No share at all (a client embed whose iframe lacks
            // allow="web-share", or Firefox, which supports no file shares
            // anywhere). A download would land in Files ▸ Downloads, four taps
            // in another app away from Photos — so show the image instead.
            this.releaseBusySoon();
            this.showSaveOverlay(dataUrl);
            return;
        }

        this.releaseBusySoon();
        this.download(file);
    }

    /** Re-arm the camera button after the rungs that finish immediately. */
    private releaseBusySoon(): void {
        window.clearTimeout(this.busyTimer);
        this.busyTimer = window.setTimeout(() => { this.busy = false; }, SHOT_DEBOUNCE_MS);
    }

    /**
     * Rung 2 — the OS share sheet. Called as the first statement after the
     * capture; nothing may be awaited before it.
     */
    private shareFile(file: File, dataUrl: string): void {
        const nav = navigator as ShareNavigator;
        const t0 = performance.now();
        // Files-only dict, deliberately: adding title/text/url is widely
        // reported to make iOS classify the share as a text/URL share, which
        // drops "Save Image" off the sheet — the one row that reaches Photos.
        // No toast on success: share() resolves without telling us which target
        // the user picked, so "Saved to Photos" would be a coin-flip lie.
        let pending: Promise<void> | undefined;
        try {
            pending = nav.share?.({ files: [file] });
        } catch {
            // A TypeError for an unsupported payload is thrown, not rejected,
            // and would otherwise escape the click handler entirely.
            pending = undefined;
        }
        if (!pending) {
            this.releaseBusySoon();
            this.showSaveOverlay(dataUrl);
            return;
        }

        // `busy` is now owned by the promise, not by a timer: a sheet stays open
        // for as long as the user wants it, and a 400ms release would let a
        // second tap run a whole second capture and a second share() whose
        // InvalidStateError we deliberately swallow.
        window.clearTimeout(this.busyTimer);
        this.busyTimer = window.setTimeout(() => { this.busy = false; }, SHARE_STUCK_MS);

        pending.then(() => {
            this.busy = false;
        }).catch((err: unknown) => {
            this.busy = false;
            const name = (err as { name?: string })?.name;
            // A sheet is already open — the debounce is the fix, not a message.
            if (name === 'InvalidStateError') return;
            // The user cancelled a sheet they actually saw. (The spec makes
            // "cancelled" and "no targets" indistinguishable; both are silent.)
            if (name === 'AbortError' && performance.now() - t0 >= SHEET_MIN_MS) return;
            // NotAllowedError, TypeError, or an AbortError too fast for any
            // sheet to have appeared: the platform failed invisibly. Never
            // retry share() — it consumes the activation — go to the rung that
            // needs no activation at all.
            this.showSaveOverlay(dataUrl);
        });
    }

    /** Rung 4 — the rescue overlay, the only path to Photos without a sheet. */
    private showSaveOverlay(dataUrl: string): void {
        // The ui root, so the card is a direct child of .ollo-ui and gets pointer
        // events back (.ollo-ui itself is pointer-events: none). NO document.body
        // fallback: if there is no parent the viewer is gone, and a card pinned to
        // the page's own bottom edge — outliving the instance that could close it —
        // is strictly worse than no card.
        const parent = this.root.parentElement;
        if (this.disposed || !parent) return;
        const t = TOAST[resolveLang()];
        this.saveOverlay?.dispose();
        this.saveOverlay = new SaveOverlay({
            parent,
            src: dataUrl,
            message: t.holdToSave,
            closeLabel: t.close
        });
    }

    /** Rung 5 — a real file on disk. The desktop convention. */
    private download(file: File): void {
        const t = TOAST[resolveLang()];
        try {
            // File-backed (not Blob-backed) object URL so Safari names the
            // download from file.name instead of inventing "unknown.txt".
            const url = URL.createObjectURL(file);
            this.objectUrls.push(url);
            const a = document.createElement('a');
            a.href = url;
            a.download = file.name;
            // The anchor MUST be in the document — WebKit has historically
            // ignored .click() on a detached one, and the old code never
            // appended it.
            document.body.appendChild(a);
            a.click();
            a.remove();
            const timerId = window.setTimeout(() => this.revoke(url, timerId), URL_LIFETIME_MS);
            this.urlTimers.push(timerId);
            // Unconditional, not only when embedded: the browser's own download
            // bubble renders on the TOP-LEVEL window, so inside the homepage
            // modal the user never sees it — and the modal is the busiest
            // surface. a.click() also returns void whether or not the download
            // was blocked, so this is the only signal we control.
            this.showToast(t.shotSaved, SHOT_TOAST_MS);
        } catch {
            this.showToast(t.shotFailed, SHOT_TOAST_MS);
        }
    }

    private revoke(url: string, timerId: number): void {
        // Drop the fired timer as well as the URL, or the list grows by one dead
        // id per capture for the life of the viewer.
        const t = this.urlTimers.indexOf(timerId);
        if (t >= 0) this.urlTimers.splice(t, 1);
        const i = this.objectUrls.indexOf(url);
        if (i < 0) return;
        this.objectUrls.splice(i, 1);
        URL.revokeObjectURL(url);
    }

    /**
     * A 140ms white wipe over the scene. Language-free and instant, so it is the
     * capture confirmation on every rung — including the share rung, where the
     * rising OS sheet may partially cover it.
     */
    private flashShutter(): void {
        const host = this.root.parentElement;
        if (this.disposed || !host) return;
        this.shutter?.remove();

        const el = document.createElement('div');
        // The white fade is a CSS ANIMATION on .ollo-shutter, so it plays from
        // the moment the element is inserted and there is no JS timing to get
        // wrong. The first cut inserted it lit and dropped the class inside a
        // requestAnimationFrame — but rAF runs BEFORE style recalc, so the
        // element's first computed opacity was already 0 and the flash never
        // painted on any platform. The element is then removed rather than
        // parked at opacity 0: an always-present full-bleed layer is a
        // compositing cost the idle viewer must not pay, and Phase 4's mobile
        // frame budget is the reason this feature avoids preserveDrawingBuffer
        // in the first place.
        el.className = 'ollo-shutter';
        host.appendChild(el);
        this.shutter = el;

        window.clearTimeout(this.shutterTimer);
        this.shutterTimer = window.setTimeout(() => {
            el.remove();
            if (this.shutter === el) this.shutter = null;
        }, 260);
    }

    private toggleFullscreen(): void {
        const el = this.container as FsElement;
        const doc = document as FsDocument;

        // Inside OUR embed the Fullscreen API is never called, on any device.
        // Fullscreen requested from a cross-origin iframe makes the browser
        // overlay a permanent exit button in the top-LEFT corner, right on top
        // of the scene name — browser chrome, not movable, not styleable, and
        // absent from the standalone viewer on the same device, which is what
        // identified it. Not making the call is the only way it stays away, so
        // we ask the parent to expand its panel instead.
        //
        // This deliberately carries NO device test. The first version gated on
        // `pointer: coarse`, which iPadOS Safari reports as `fine` the moment a
        // trackpad or Magic Keyboard is attached — so Safari fell through to
        // the real API and got the button back while Chrome on the same iPad
        // was clean. A capability we cannot read reliably is not one to branch
        // on; the embed behaves the same everywhere instead.
        if (ownsFullscreen()) {
            this.goPseudoFullscreen();
            return;
        }

        // The old code called container.requestFullscreen() unconditionally —
        // on iPhone Safari that method simply doesn't exist, so the call threw
        // a synchronous TypeError (the promise .catch never engaged) and the
        // button read as dead. Hence the capability ladder below.
        if (fullscreenApiAvailable(el)) {
            const active = document.fullscreenElement ?? doc.webkitFullscreenElement ?? null;
            try {
                if (!active) {
                    if (typeof el.requestFullscreen === 'function') {
                        el.requestFullscreen().catch(() => {});
                    } else {
                        void el.webkitRequestFullscreen?.();
                    }
                } else if (typeof document.exitFullscreen === 'function') {
                    document.exitFullscreen().catch(() => {});
                } else {
                    void doc.webkitExitFullscreen?.();
                }
            } catch { /* API present but refused — nothing sensible to do */ }
            return;
        }

        // No Fullscreen API (iPhone — every iPhone browser is WebKit, so this
        // includes Chrome/Firefox/Edge there).
        if (window.parent === window) return; // standalone: button not shown anyway

        // Embedded in the homepage modal: the parent can still give the panel
        // the whole screen — ask it to.
        if (embeddedByOlloport()) {
            this.goPseudoFullscreen();
            return;
        }

        // Embedded on someone else's site (first case: a client's WordPress
        // page). Nobody there listens for our message, so the closest thing to
        // fullscreen is the viewer standalone in its own tab — on iPhone that
        // fills the screen. Must stay synchronous inside the tap handler or
        // Safari's popup blocker eats it. noopener: the new tab needs nothing
        // from us, and it keeps a hostile embedder from reaching back.
        window.open(window.location.href, '_blank', 'noopener');
    }

    /** `ms` exists because a full sentence ("No s’ha pogut desar la imatge")
     *  cannot be read in the 1500ms the link toasts use. */
    showToast(message: string, ms = 1500): void {
        if (this.disposed) return;
        this.toast?.remove();
        window.clearTimeout(this.toastTimer);
        const t = document.createElement('div');
        t.className = 'ollo-toast';
        t.textContent = message;
        this.root.appendChild(t);
        this.toast = t;
        // The identity guard keeps an older timer from killing a newer toast.
        this.toastTimer = window.setTimeout(() => {
            if (this.toast === t) {
                t.remove();
                this.toast = null;
            }
        }, ms);
    }

    dispose(): void {
        // The editor preview re-mounts, so everything this instance owns has to
        // go: previously the Toolbar held only DOM that container.innerHTML = ''
        // wiped, but it now holds timers, object URLs and an overlay.
        //
        // Set FIRST, and checked by every deferred path: an in-flight share()
        // can reject minutes after this runs (the sheet is still open when the
        // preview re-mounts), and that rejection would otherwise build a rescue
        // card no one owns.
        this.disposed = true;
        this.rowObserver?.disconnect();
        if (this.onDocDown) document.removeEventListener('pointerdown', this.onDocDown);
        window.clearTimeout(this.toastTimer);
        window.clearTimeout(this.busyTimer);
        window.clearTimeout(this.shutterTimer);
        window.clearInterval(this.readyTimer);
        for (const id of this.urlTimers) window.clearTimeout(id);
        this.urlTimers = [];
        for (const url of this.objectUrls) URL.revokeObjectURL(url);
        this.objectUrls = [];
        this.saveOverlay?.dispose();
        this.saveOverlay = null;
        this.shutter?.remove();
        this.shutter = null;
        this.busy = false;
        this.toast?.remove();
        if (this.onFsChange) {
            document.removeEventListener('fullscreenchange', this.onFsChange);
            document.removeEventListener('webkitfullscreenchange', this.onFsChange);
            window.removeEventListener('resize', this.onFsChange);
            this.onFsChange = null;
        }
        this.closeBtn?.remove();
        this.closeBtn = null;
        if (this.onFieldEngage) {
            this.container.removeEventListener('pointerdown', this.onFieldEngage, true);
            this.container.removeEventListener('focusin', this.onFieldEngage);
            this.onFieldEngage = null;
        }
        this.root.remove();
    }
}
